/**
 * Home-screen Arena components:
 *   - ArenaTopBar: thin gold top line + "Arena Mode · {gym}" status (only during active check-in)
 *   - ArenaHomeTile: card shown to linked members (no Arena Pass)
 *   - ArenaScannerLauncher: top-right QR scanner shortcut + manual code modal
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { QrCode, MapPin } from "lucide-react";
import { ArenaShield } from "./ArenaAtoms";
import QRScanner from "./QRScanner";

export function ArenaTopBar({ gymName }) {
  if (!gymName) return null;
  return (
    <div data-testid="arena-mode-bar" style={{ position: "relative" }}>
      <div style={{ height: 2, background: "linear-gradient(90deg, #F5C518 0%, #E4A30C 50%, #F5C518 100%)" }} />
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        padding: "6px 14px",
        background: "linear-gradient(180deg, rgba(245,197,24,0.16) 0%, rgba(245,197,24,0.06) 100%)",
        borderBottom: "1px solid rgba(245,197,24,0.30)",
      }}>
        <ArenaShield size={14} showLabel={false} />
        <span style={{ fontFamily: "Barlow", fontSize: 12, fontWeight: 600, color: "#A37B00" }}>{gymName} · Active</span>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22C55E", boxShadow: "0 0 6px rgba(34,197,94,0.6)" }} />
      </div>
    </div>
  );
}

export function ArenaHomeTile({ gym }) {
  const navigate = useNavigate();
  if (!gym) return null;
  const open = gymOpenStatus(gym);
  return (
    <button
      onClick={() => navigate(`/arena/${gym.gym_code}`)}
      data-testid="arena-home-tile"
      style={{
        textAlign: "left", padding: 16,
        background: "var(--bg-surface, #E4E2DE)",
        border: "1px solid var(--border, rgba(12,12,11,0.10))",
        borderRadius: 16, display: "flex", gap: 14, alignItems: "center", width: "100%",
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        background: "rgba(245,197,24,0.16)", display: "grid", placeItems: "center",
      }}>
        <ArenaShield size={22} showLabel={false} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Barlow Condensed", fontSize: 16, fontWeight: 800 }}>{gym.gym_name} <span style={{ color: "#A37B00" }}>Arena</span></div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <MapPin size={10} /> {gym.city} · <span style={{ color: open ? "#22C55E" : "#A52121", fontWeight: 700 }}>{open ? "Open now" : "Closed"}</span>
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#A37B00" }}>Check in →</div>
    </button>
  );
}

export function ArenaScannerLauncher() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Scan gym QR"
        data-testid="arena-scanner-launcher"
        style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(245,197,24,0.16)",
          color: "#0C0C0B",
          border: "1px solid rgba(245,197,24,0.45)",
          display: "grid", placeItems: "center",
        }}
      >
        <QrCode size={16} />
      </button>
      <QRScanner open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function gymOpenStatus(gym) {
  const wh = gym.working_hours;
  if (!wh || !Array.isArray(wh)) return true;
  const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date().getDay()];
  const today = wh.find((w) => w.day === dayName) || {};
  if (today.closed) return false;
  if (!today.open || !today.close) return true;
  const now = new Date();
  const m = now.getHours() * 60 + now.getMinutes();
  const [oh, om] = (today.open || "0:0").split(":").map((n) => parseInt(n, 10) || 0);
  const [ch, cm] = (today.close || "23:59").split(":").map((n) => parseInt(n, 10) || 0);
  return m >= oh * 60 + om && m <= ch * 60 + cm;
}
